import {
  camelCaseObjectKeys,
  snakeCaseObjectKeys,
} from '@doordash/object-utils'
import axios, { AxiosRequestConfig } from 'axios'
import { get } from 'lodash'
import { SupportedCountryCode, WorkflowStepStatus } from 'src/models/types'
import { getToken } from '../utilities/configureApi'

export type GeoLocator = {
  zip: string
  country: string
  locale?: string
}

export type PersonLocator = {
  id?: { value: string }
  email: string
  phoneNumber: string
}

export type Onboarding = {
  onboardingId: string
  workflow: Workflow
}

export type Workflow = {
  currentModuleId: { value: string }
  currentStepId: { value: string }
  currentStepStatus: WorkflowStepStatus
  template: { value: string }
}

export type CreateOnboardingRequest = {
  geoLocator: GeoLocator
  personLocator: PersonLocator
}

export type CreateOnboardingResponse = {
  onboarding: Onboarding
  personLocator: PersonLocator
}

const dxApiUrl = (): string | undefined => get(window, 'view.DX_API')
const opts: AxiosRequestConfig = { baseURL: dxApiUrl() }

const generateUpdatePayload = (uniqueLink, data, currentPageTemplate) => {
  const requestData = {
    person_locator: {
      id: uniqueLink,
    },
    current_module_id: currentPageTemplate.module_id,
    current_step_id: currentPageTemplate.step_id,
    event: {
      actions_list: Object.entries(data).map(([inputId, jsonData]) => ({
        input_id: inputId,
        json_data: get(jsonData, 'json_data'),
        input_type: inputId,
      })),
    },
  }

  return requestData
}

/*
    TODO: We dont need 2 separate payloads(actionsList vs actions_list) for VGS submit
    vs regular submit. Merge once BE updates the paths.
*/
const generateUpdatePayloadForVgs = (uniqueLink, data, currentPageTemplate) => {
  const requestData = {
    person_locator: {
      id: uniqueLink,
    },
    current_module_id: currentPageTemplate.module_id,
    current_step_id: currentPageTemplate.step_id,
    event: {
      actionsList: Object.entries(data).map(([inputId, jsonData]) => ({
        input_id: inputId,
        json_data: get(jsonData, 'json_data'),
        input_type: inputId,
      })),
    },
  }

  return requestData
}

export const getOnboarding = async (
  uniqueLink: string,
  countryCode: SupportedCountryCode
) => {
  const url = `/v1/dasher-onboarding`
  const response = await axios.get(url, {
    ...opts,
    params: snakeCaseObjectKeys({ uniqueLink, countryCode }),
  })
  return camelCaseObjectKeys(response.data.onboardings)
}

export const createOnboarding = async (
  data: CreateOnboardingRequest
): Promise<CreateOnboardingResponse> => {
  const url = `/v1/dasher-onboarding/create`
  const response = await axios.post(url, snakeCaseObjectKeys(data), opts)
  return camelCaseObjectKeys(response.data)
}

export const updateOnboarding = async (
  uniqueLink,
  data,
  currentPageTemplate
) => {
  const url = `/v1/dasher-onboarding`
  const requestData = generateUpdatePayload(
    uniqueLink,
    data,
    currentPageTemplate
  )

  const response = await axios.post(url, requestData, opts)
  return camelCaseObjectKeys(response.data).onboarding
}

export const updateOnboardingViaVGS = async (
  uniqueLink,
  data,
  currentPageTemplate,
  form
) => {
  const url = '/in/portal/dx-api/v1/dasher-onboarding'
  const res = await new Promise((resolve, reject) => {
    form.submit(
      url,
      {
        mapDotToObject: 'mergeArray',
        headers: {
          Authorization: `${getToken(true)}`,
        },
        data: generateUpdatePayloadForVgs(
          uniqueLink,
          data,
          currentPageTemplate
        ),
      },
      (status, response) => {
        if (status > 399) {
          reject(response)
        } else {
          resolve(camelCaseObjectKeys(response).onboarding)
        }
      },
      error => {
        reject(error)
      }
    )
  })

  return res
}
